import React, { useState, useEffect } from 'react';
import { displayStaffName, STAFF_FALLBACK } from '../../lib/customerIdentity';
import { formatInTenantTz } from '../../lib/datetime';
import { isMeaningfulResolution } from '../../lib/solutionResolution';
import { auth } from '../../lib/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useBusiness } from '../../contexts/BusinessContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../hooks/use-toast';
import {
  Search,
  Filter,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  UserPlus,
  UserCheck,
  Trash2,
  Calendar,
  Phone,
  ArrowRight,
  Loader2,
  Plus,
  CheckCheck
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { tenantHeaders } from '../../lib/tenantSession';

// Separate role sets matching backend — super_admin is NOT a tenant ticket role
const _ASSIGN_ROLES = new Set(['admin', 'administrator', 'manager']);
const _DELETE_ROLES = new Set(['admin', 'administrator', 'manager']);

// A (display truthfulness safety net): only render a value that looks like a real dialable phone.
// Synthetic placeholders (`user-<firebase_uid>`), UID-like or otherwise non-dialable values are
// hidden as "—". This never substitutes another customer's number; it only refuses to show a
// non-phone. The backend projection already blanks these; this is the frontend defensive net.
const _isDisplayablePhone = (p) => {
  const s = (p == null ? '' : String(p)).trim();
  return s !== '' && /^\+?[0-9][0-9()\-\s.]{5,}$/.test(s);
};

const TicketsManager = () => {
  const { getBusinessConversations, currentBusiness } = useBusiness();
  const { user: authUser } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();

  // Add safety check for business context
  if (!currentBusiness) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">{t('ops.tickets.loadingBusinessData')}</h3>
          <p className="text-gray-600">{t('ops.tickets.loadingBusinessInfo')}</p>
        </div>
      </div>
    );
  }
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');   // BUS-1: priority filter
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedTicketMessages, setSelectedTicketMessages] = useState(null);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [businessConversations, setBusinessConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondOpen, setRespondOpen] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalateReason, setEscalateReason] = useState('');
  const [error, setError] = useState(null);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Assignment / lifecycle state
  const [myStaffId, setMyStaffId] = useState(null);
  const [myRole, setMyRole] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [ticketDataByConvId, setTicketDataByConvId] = useState({});
  const [myQueueOnly, setMyQueueOnly] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState('');
  const [assignTicketId, setAssignTicketId] = useState('');
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [reassignTicketId, setReassignTicketId] = useState('');

  // New ticket form state
  const [newTicketData, setNewTicketData] = useState({
    title: '',
    customer: '',
    category: '',
    priority: 'medium',
    description: ''
  });

  // GOV-7 governed resolve — Mark Resolved opens a dialog requiring MEANINGFUL reusable
  // resolution steps (solution_resolution). The backend creates the linked Solution candidate
  // (source=human_resolution, pending_approval) BEFORE the ticket is marked resolved. A direct
  // hardcoded resolve (the old behaviour) never produced a Solution candidate.
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveData, setResolveData] = useState({ resolution: '', solutionTitle: '', resolutionSteps: '' });

  const _getToken = async () => {
    try { if (auth.currentUser) return await auth.currentUser.getIdToken(); } catch {}
    return localStorage.getItem('crm_access_token') || '';
  };

  // Load current operator's staff_id + full staff list for assign picker
  const loadMyStaff = async () => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const token = await _getToken();
    const currentUid = auth.currentUser?.uid || '';
    const currentPhone = (auth.currentUser?.phoneNumber || '').replace(/\D/g, '');
    try {
      const resp = await fetch(
        `${base}/api/v1/billing/staff/by-brand/${currentBusiness.brandId}`,
        { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
      );
      if (resp.ok) {
        const sd = await resp.json();
        const list = sd.staff || [];
        setStaffList(list);
        const match = list.find(s => {
          if (currentUid && s.firebase_uid === currentUid) return true;
          const sPhone = (s.phone || '').replace(/\D/g, '');
          if (currentPhone && sPhone && sPhone === currentPhone) return true;
          if (currentUid && s.id === currentUid) return true;
          return false;
        });
        if (match) {
          setMyStaffId(match.id);
          setMyRole(match.role || '');
        }
      }
    } catch (_) {}
  };

  // Load tickets and build conv_id → ticket map for assignment enrichment.
  // Returns the map directly so callers can use it immediately (React setState is async).
  const loadTicketData = async () => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const token = await _getToken();
    try {
      const resp = await fetch(
        `${base}/api/v1/tickets/business/${currentBusiness.brandId}`,
        { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
      );
      if (resp.ok) {
        const d = await resp.json();
        const map = {};
        for (const t of (d.tickets || [])) {
          if (t.conversation_id) map[t.conversation_id] = t;
          if (t.id) map[t.id] = t;
        }
        setTicketDataByConvId(map);
        return map;
      }
    } catch (_) {}
    return {};
  };

  // Merge ticket assignment data into a conversation record
  const _mergeTicket = (conv) => {
    const td = ticketDataByConvId[conv.id] || ticketDataByConvId[conv.ticket_id];
    if (!td) return conv;
    return {
      ...conv,
      ticket_id:        td.ticket_id || td.id,
      assigned_to:      td.assigned_to || conv.assigned_to,
      assigned_to_name: td.assigned_to_name,
      assigned_by:      td.assigned_by,
      assigned_at:      td.assigned_at,
      accepted_at:      td.accepted_at,
      accepted_by:      td.accepted_by,
      resolved_at:      td.resolved_at,
      resolved_by:      td.resolved_by,
      ticket_status:    td.status,
      resolution:       td.resolution || conv.resolution,
      resolution_notes: td.resolution_notes,
    };
  };

  // Assignment: management opens assign dialog
  const openAssignDialog = (ticket) => {
    const tid = ticket.ticket_id || ticket.id;
    if (!tid || !tid.startsWith('TKT-')) {
      toast({ title: t('ops.tickets.noTicketId'), description: t('ops.tickets.noTicketIdCreate'), variant: "destructive" });
      return;
    }
    setAssignTicketId(tid);
    setAssignTarget('');
    setAssignDialogOpen(true);
  };

  const handleAssignTicket = async () => {
    if (!assignTarget) {
      toast({ title: t('ops.tickets.selectAnOperator'), description: t('ops.tickets.chooseStaffAssign'), variant: "destructive" });
      return;
    }
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const token = await _getToken();
      const resp = await fetch(`${base}/api/v1/tickets/${assignTicketId}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
        body: JSON.stringify({ assigned_to: assignTarget }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      toast({ title: t('ops.tickets.ticketAssigned'), description: t('ops.tickets.ticketAssignedDesc') });
      setAssignDialogOpen(false);
      await loadTicketData();
      loadConversations();
    } catch (e) {
      toast({ title: t('ops.tickets.assignFailed'), description: e.message, variant: "destructive" });
    }
  };

  // Accept: operator transitions assigned → in_progress
  const handleAcceptTicket = async (ticket) => {
    const tid = ticket.ticket_id || ticket.id;
    if (!tid || !tid.startsWith('TKT-')) {
      toast({ title: t('ops.tickets.noTicketId'), description: t('ops.tickets.noTicketAccept'), variant: "destructive" });
      return;
    }
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const token = await _getToken();
      const resp = await fetch(`${base}/api/v1/tickets/${tid}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      toast({ title: t('ops.tickets.ticketAccepted'), description: t('ops.tickets.ticketAcceptedDesc') });
      await loadTicketData();
      loadConversations();
    } catch (e) {
      toast({ title: t('ops.tickets.acceptFailed'), description: e.message, variant: "destructive" });
    }
  };

  // Delete: management removes invalid guest tickets
  const handleDeleteInvalidGuest = async (ticket) => {
    const tid = ticket.ticket_id || ticket.id;
    if (!tid || !tid.startsWith('TKT-')) {
      toast({ title: t('ops.tickets.noTicketId'), description: t('ops.tickets.noTicketDelete'), variant: "destructive" });
      return;
    }
    if (!window.confirm(t('ops.tickets.deleteConfirm', { tid }))) return;
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const token = await _getToken();
      const resp = await fetch(`${base}/api/v1/tickets/${tid}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      toast({ title: t('ops.tickets.ticketDeleted'), description: t('ops.tickets.ticketDeletedDesc', { tid }) });
      await loadTicketData();
      loadConversations();
    } catch (e) {
      toast({ title: t('ops.tickets.deleteFailed'), description: e.message, variant: "destructive" });
    }
  };

  // Reassign: operator reassigns their own ticket; management can reassign any
  const openReassignDialog = (ticket) => {
    const tid = ticket.ticket_id || ticket.id;
    if (!tid || !tid.startsWith('TKT-')) {
      toast({ title: t('ops.tickets.noTicketId'), description: t('ops.tickets.noTicketLinked'), variant: "destructive" });
      return;
    }
    setReassignTicketId(tid);
    setReassignTarget('');
    setReassignReason('');
    setReassignDialogOpen(true);
  };

  const handleReassignTicket = async () => {
    if (!reassignTarget) {
      toast({ title: t('ops.tickets.selectOperatorTitle'), description: t('ops.tickets.chooseOperatorReassign'), variant: "destructive" });
      return;
    }
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const token = await _getToken();
      const resp = await fetch(`${base}/api/v1/tickets/${reassignTicketId}/reassign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
        body: JSON.stringify({ target_staff_id: reassignTarget, reason: reassignReason }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      toast({ title: t('ops.tickets.ticketReassigned'), description: t('ops.tickets.ticketReassignedDesc') });
      setReassignDialogOpen(false);
      await loadTicketData();
      loadConversations();
    } catch (e) {
      toast({ title: t('ops.tickets.reassignFailed'), description: e.message, variant: "destructive" });
    }
  };

  const _INVALID_DISPLAY_NAMES = new Set([
    'guest', 'unknown', 'anonymous', 'anonymous_customer', 'anon_customer',
    'undefined', 'null', 'none', 'customer', 'default_customer', 'ai_chat',
  ]);

  const getDisplayName = (ticket) => {
    const raw = (ticket.customer_name || ticket.customerName || '').trim();
    if (!raw || _INVALID_DISPLAY_NAMES.has(raw.toLowerCase()) || /^[A-Za-z0-9]{20,36}$/.test(raw)) {
      // D3: never surface a raw customer_id as a name — honest generic label only.
      return 'Unknown Customer';
    }
    return raw;
  };

  const loadTicketMessages = async (ticket) => {
    const convId = ticket?.id;
    if (!convId) return;
    setMessagesLoading(true);
    setSelectedTicketMessages(null);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const doFetch = async (forceRefresh = false) => {
        let token = '';
        try {
          if (auth.currentUser) token = await auth.currentUser.getIdToken(forceRefresh);
        } catch (_) {}
        if (!token) token = localStorage.getItem('crm_access_token') || '';
        const headers = token ? { Authorization: `Bearer ${token}`, ...tenantHeaders() } : {};
        return fetch(`${base}/api/v1/conversations/${convId}/messages`, { headers });
      };
      let resp = await doFetch(false);
      if (resp.status === 401) resp = await doFetch(true);
      if (resp.ok) {
        const msgs = await resp.json();
        setSelectedTicketMessages(Array.isArray(msgs) ? msgs : []);
      } else {
        setSelectedTicketMessages([]);
      }
    } catch (_) {
      setSelectedTicketMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  // Load conversations on mount, then poll every 15 seconds so new customer tickets appear without refresh
  useEffect(() => {
    loadConversations();
    loadMyStaff();
    const pollInterval = setInterval(() => { loadConversations(); loadTicketData(); }, 15000);

    // Check for new conversation customer from other components
    const newCustomerData = sessionStorage.getItem('newConversationCustomer');
    if (newCustomerData) {
      try {
        const customer = JSON.parse(newCustomerData);
        // Pre-fill new ticket form with customer data
        setNewTicketData({
          title: `Conversation with ${customer.name}`,
          customer: customer.name,
          category: 'general', // Use valid category value
          priority: 'medium',
          description: '' // Blank description for user to fill
        });
        // Open new ticket dialog
        setNewTicketOpen(true);
        // Clear the sessionStorage
        sessionStorage.removeItem('newConversationCustomer');
      } catch (error) {
        console.error('Error parsing customer data:', error);
      }
    }

    return () => clearInterval(pollInterval);
  }, []);

  // Re-merge a single conversation record with the latest ticket map.
  // Used after every poll refresh to keep selectedTicket's ticket_id and lifecycle
  // fields accurate without requiring the user to re-click.
  const _mergeWithMap = (conv, map) => {
    if (!map || !conv) return conv;
    const td = map[conv.id] || map[conv.ticket_id];
    if (!td) return conv;
    return {
      ...conv,
      ticket_id:          td.ticket_id || td.id,
      assigned_to:        td.assigned_to || conv.assigned_to,
      assigned_to_name:   td.assigned_to_name,
      assigned_by:        td.assigned_by,
      assigned_at:        td.assigned_at,
      accepted_at:        td.accepted_at,
      accepted_by:        td.accepted_by,
      resolved_at:        td.resolved_at,
      resolved_by:        td.resolved_by,
      ticket_status:      td.status,
      resolution:         td.resolution || conv.resolution,
      resolution_notes:   td.resolution_notes,
    };
  };

  const loadConversations = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!getBusinessConversations || typeof getBusinessConversations !== 'function') {
        throw new Error('getBusinessConversations function is not available');
      }

      const conversations = await getBusinessConversations();
      const latestMap = await loadTicketData();

      // Sort: escalated/assigned first, then active, then resolved
      const _statusRank = (s) => {
        if (['escalated', 'assigned', 'receiving_attention'].includes(s)) return 0;
        if (['active', 'ai_processing', 'pending'].includes(s)) return 1;
        return 2;
      };
      const sorted = Array.isArray(conversations)
        ? [...conversations].sort((a, b) => _statusRank(a.status) - _statusRank(b.status))
        : [];

      setBusinessConversations(sorted);
      // Re-merge the currently selected ticket with fresh ticket lifecycle data
      // so ticket_id, assigned_to etc. stay accurate after every poll refresh.
      setSelectedTicket(sel => {
        if (!sel) return null;
        const fresh = sorted.find(t => t.id === sel.id) || sel;
        return _mergeWithMap(fresh, latestMap);
      });
    } catch (err) {
      console.error('Error loading conversations:', err);
      setError('Failed to load conversations');
      toast({
        title: t('common.error'),
        description: t('ops.tickets.failedLoadTickets'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'receiving_attention':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'more_details_required':
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'receiving_attention':
        return 'bg-blue-100 text-blue-800';
      case 'more_details_required':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return formatInTenantTz(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCreateTicket = async () => {
    if (!newTicketData.title || !newTicketData.customer || !newTicketData.description) {
      toast({ title: t('ops.common.missingInformation'), description: t('ops.common.fillAllRequired'), variant: "destructive" });
      return;
    }
    try {
      const token = await _getToken();
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const resp = await fetch(`${base}/api/v1/tickets/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
        body: JSON.stringify({
          customer_name: newTicketData.customer,
          title: newTicketData.title,
          description: newTicketData.description,
          priority: newTicketData.priority,
          category: newTicketData.category || 'general',
          business_id: currentBusiness?.brandId || '',
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create ticket');
      }
      toast({ title: t('ops.tickets.ticketCreated'), description: t('ops.tickets.ticketCreatedDesc') });
      setNewTicketData({ title: '', customer: '', category: '', priority: 'medium', description: '' });
      setNewTicketOpen(false);
      loadConversations();
    } catch (error) {
      toast({ title: t('common.error'), description: error.message || t('ops.tickets.failedCreate'), variant: "destructive" });
    }
  };

  const handleRespond = () => {
    if (!selectedTicket) return;
    setRespondOpen(true);
  };

  const handleSendResponse = async () => {
    if (!responseMessage.trim()) {
      toast({ title: t('ops.tickets.emptyMessage'), description: t('ops.tickets.enterResponse'), variant: "destructive" });
      return;
    }
    if (!selectedTicket?.id) return;

    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const token = await _getToken();
      const resp = await fetch(`${base}/api/v1/conversations/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
        body: JSON.stringify({
          conversation_id: selectedTicket.id,
          sender_id: 'staff',
          sender_type: 'operator',
          message: responseMessage,
          message_type: 'operator',
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      toast({ title: t('ops.tickets.responseSent'), description: t('ops.tickets.messageSentSuccess') });
      setResponseMessage('');
      setRespondOpen(false);
      // Reload messages
      loadTicketMessages(selectedTicket);
    } catch (error) {
      toast({ title: t('common.error'), description: error.message || t('ops.tickets.failedSendResponse'), variant: "destructive" });
    }
  };

  const handleEscalate = () => {
    if (!selectedTicket) return;
    setEscalateOpen(true);
  };

  const handleSendEscalation = async () => {
    if (!escalateReason.trim()) {
      toast({ title: t('ops.tickets.reasonRequired'), description: t('ops.tickets.provideEscReason'), variant: "destructive" });
      return;
    }
    if (!selectedTicket?.id) return;
    try {
      const token = await _getToken();
      const base = process.env.REACT_APP_BACKEND_URL || '';
      // Update conversation status to escalated
      const statusResp = await fetch(
        `${base}/api/v1/conversations/${selectedTicket.id}/status?status=escalated`,
        { method: 'PUT', headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
      );
      if (!statusResp.ok) {
        const err = await statusResp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${statusResp.status}`);
      }
      // Log escalation reason as internal note
      await fetch(`${base}/api/v1/conversations/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
        body: JSON.stringify({
          conversation_id: selectedTicket.id,
          sender_id: 'staff',
          sender_type: 'operator',
          message: `[Escalation] ${escalateReason}`,
          message_type: 'internal',
        }),
      });
      toast({ title: t('ops.tickets.ticketEscalated'), description: t('ops.tickets.ticketEscalatedDesc') });
      setEscalateReason('');
      setEscalateOpen(false);
      loadConversations();
    } catch (error) {
      toast({ title: t('common.error'), description: error.message || t('ops.tickets.failedEscalate'), variant: "destructive" });
    }
  };

  // GOV-7 governed resolve — Mark Resolved no longer does a direct hardcoded PUT. It OPENS the
  // governed resolve dialog so the operator authors a meaningful, reusable resolution
  // (solution_resolution) that the backend turns into a Solution candidate before finalizing.
  const handleMarkResolved = () => {
    if (!selectedTicket) return;
    setResolveData({
      resolution: '',
      solutionTitle: selectedTicket.subject || selectedTicket.intent || '',
      resolutionSteps: '',
    });
    setResolveDialogOpen(true);
  };

  const submitGovernedResolve = async () => {
    if (!selectedTicket?.id) return;
    // ONE gate, applied to BOTH the ticket-resolve and conversation-status paths: blank/placeholder/
    // status-only text is blocked UP FRONT — the ticket is NOT resolved and no Solution candidate is
    // created (no silent finalize). Backend remains authoritative; this is the immediate operator signal.
    if (!isMeaningfulResolution(resolveData.resolutionSteps)) {
      toast({
        title: t('ops.tickets.reasonRequired'),
        description: 'Enter the specific, reusable steps that resolved this issue (not a status like "done" or "resolved").',
        variant: "destructive",
      });
      return;
    }
    const tid = selectedTicket.ticket_id || selectedTicket.id;
    try {
      const token = await _getToken();
      const base = process.env.REACT_APP_BACKEND_URL || '';
      // PUT /tickets/{id}/resolve when a TKT- ticket exists — the backend creates the linked Solution
      // candidate (source=human_resolution + conversation/ticket/resolve-event linkage stamped
      // server-side) BEFORE marking the ticket resolved. A non-reusable resolution → 422 → the ticket
      // is NOT finalized (no orphan). Conversations without a persisted ticket fall back to a status
      // update, but still only AFTER the meaningful-resolution gate above.
      let resp;
      if (tid && tid.startsWith('TKT-')) {
        resp = await fetch(`${base}/api/v1/tickets/${tid}/resolve`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
          body: JSON.stringify({
            resolution: (resolveData.resolution.trim() || resolveData.resolutionSteps.trim()),
            solution_title: (resolveData.solutionTitle.trim() || selectedTicket?.subject || selectedTicket?.intent || 'Resolved issue').slice(0, 80),
            solution_resolution: resolveData.resolutionSteps.trim(),
          }),
        });
      } else {
        resp = await fetch(
          `${base}/api/v1/conversations/${selectedTicket.id}/status?status=resolved`,
          { method: 'PUT', headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
        );
      }
      if (!resp.ok) {
        // Governed transaction blocked (e.g. 422 journey-linkage/category): surface the reason,
        // keep the dialog open, do NOT show a "resolved" toast, do NOT reload as resolved.
        const err = await resp.json().catch(() => ({}));
        const detail = err && err.detail;
        const msg = typeof detail === 'string'
          ? detail
          : (detail?.message
             || (Array.isArray(detail?.validation_errors) ? detail.validation_errors.join(' ') : `HTTP ${resp.status}`));
        toast({ title: t('common.error'), description: msg || t('ops.tickets.failedResolve'), variant: "destructive" });
        return;
      }
      toast({ title: t('ops.tickets.ticketResolved'), description: t('ops.tickets.ticketResolvedDesc') });
      setResolveDialogOpen(false);
      setResolveData({ resolution: '', solutionTitle: '', resolutionSteps: '' });
      loadConversations();
      loadTicketMessages(selectedTicket);
    } catch (error) {
      toast({ title: t('common.error'), description: error.message || t('ops.tickets.failedResolve'), variant: "destructive" });
    }
  };

  const handleCallCustomer = async () => {
    if (!selectedTicket) return;
    const name = getDisplayName(selectedTicket);
    // Prefer conversation_id — the call endpoint resolves customer phone server-side
    const convId = selectedTicket.conversation_id || selectedTicket.id || selectedTicket.ticket_id;
    if (!convId) {
      toast({ title: t('ops.common.cannotCall'), description: t('ops.tickets.noConvLinked'), variant: "destructive" });
      return;
    }
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const token = await _getToken();
      const resp = await fetch(`${base}/api/v1/conversations/${convId}/call`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() },
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.success) {
        const wasInitiated = data.status === 'initiated';
        toast({
          title: wasInitiated ? t('ops.common.callInitiated') : t('ops.common.callLogged'),
          description: data.message || `${wasInitiated ? t('ops.common.calling') : t('ops.common.callRecordedFor')} ${name}`,
        });
        // Refresh ticket list so the call event appears in the thread on next open
        loadConversations();
      } else {
        throw new Error(data.detail || `HTTP ${resp.status}`);
      }
    } catch (e) {
      toast({ title: t('ops.common.callFailed'), description: e.message, variant: "destructive" });
    }
  };

  const handleMessageCustomer = () => {
    // In-app operator response — opens the respond dialog and posts to /conversations/message.
    // This is NOT email (no recipient email, no provider, no delivery).
    if (!selectedTicket) return;
    setRespondOpen(true);
  };

  const handleFiltersApply = (filterType, filterValue) => {
    if (filterType === 'status') {
      setSelectedStatus(filterValue);
    } else if (filterType === 'priority') {
      setSelectedPriority(filterValue);   // BUS-1
    }
  };

  // Merge ticket assignment data into every conversation for display
  const _convMerged = businessConversations.map(_mergeTicket);
  // EMT Blocker A — ticket-source-first: surface valid tickets from /tickets/business whose
  // conversation is NOT in the (100-capped, updated_at-sorted) conversation list, so voice/staff-created
  // tickets are never hidden on Web while Android shows them. One-thread is preserved: each orphan row
  // links back to its conversation_id, so opening it still loads GET /conversations/{id}/messages.
  const _seenIds = new Set();
  _convMerged.forEach(c => { if (c.id) _seenIds.add(c.id); if (c.ticket_id) _seenIds.add(c.ticket_id); });
  const _orphanTickets = [];
  const _pushed = new Set();
  Object.values(ticketDataByConvId || {}).forEach(t => {
    const tid = t.ticket_id || t.id;
    if (!tid || _pushed.has(tid)) return;
    if (_seenIds.has(tid) || _seenIds.has(t.conversation_id)) return; // already shown via a conversation
    _pushed.add(tid);
    _orphanTickets.push({
      id: t.conversation_id || tid,
      conversation_id: t.conversation_id || tid,
      ticket_id: tid,
      customer_name: t.customer_name,
      customer_id: t.customer_id,
      customer_phone: t.customer_phone,
      category: t.category,
      status: t.status,
      ticket_status: t.status,
      assigned_to: t.assigned_to,
      assigned_to_name: t.assigned_to_name,
      assigned_at: t.assigned_at,
      accepted_at: t.accepted_at,
      resolved_at: t.resolved_at,
      created_at: t.created_at,
      updated_at: t.updated_at,
      _ticketSourced: true,
    });
  });
  const mergedConversations = [..._convMerged, ..._orphanTickets];

  // Apply My Queue filter (operator-assigned tickets only)
  const baseList = myQueueOnly && myStaffId
    ? mergedConversations.filter(t => t.assigned_to === myStaffId)
    : mergedConversations;

  const filteredTickets = baseList.filter(ticket => {
    const displayName = ticket.customer_name || ticket.customerName || ticket.businessName || '';  // D3: no customer_id fallback
    const matchesSearch = displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (ticket.category || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (ticket.id || '').toLowerCase().includes(searchTerm.toLowerCase());
    const effectiveStatus = ticket.ticket_status || ticket.status;
    let matchesStatus = selectedStatus === 'all';
    if (!matchesStatus) {
      if (selectedStatus === 'active') {
        matchesStatus = ['active', 'ai_processing', 'receiving_attention', 'assigned', 'in_progress'].includes(effectiveStatus);
      } else if (selectedStatus === 'pending') {
        matchesStatus = ['pending', 'more_details_required', 'escalated', 'open'].includes(effectiveStatus);
      } else {
        matchesStatus = ['resolved', 'closed'].includes(effectiveStatus);
      }
    }
    // BUS-1: priority filter (canonical lowercase critical/high/medium/low; case-insensitive on stored value)
    const matchesPriority = selectedPriority === 'all'
      || (ticket.priority || '').toLowerCase() === selectedPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const statusCounts = {
    all: baseList.length,
    active: baseList.filter(t => ['active', 'ai_processing', 'receiving_attention', 'assigned', 'in_progress'].includes(t.ticket_status || t.status)).length,
    pending: baseList.filter(t => ['pending', 'more_details_required', 'escalated', 'open'].includes(t.ticket_status || t.status)).length,
    resolved: baseList.filter(t => ['resolved', 'closed'].includes(t.ticket_status || t.status)).length,
  };

  return (
    <div className="flex-1 h-full flex flex-col overflow-hidden bg-gradient-to-br from-white via-green-50 to-purple-50">
      {/* Header */}
      <div className="shrink-0 p-6 bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('page.tickets.title')}</h1>
            <p className="text-gray-600">{t('page.tickets.subtitle', { business: currentBusiness?.businessName || '' })}</p>
          </div>
          <div className="flex space-x-3">
            <Dialog open={newTicketOpen} onOpenChange={setNewTicketOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('common.newTicket')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t('ops.tickets.createNewTicket')}</DialogTitle>
                  <DialogDescription>
                    {t('ops.tickets.createTicketDesc')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="title">{t('ops.tickets.titleLabel')}</Label>
                    <Input
                      id="title"
                      placeholder={t('ops.tickets.titlePlaceholder')}
                      value={newTicketData.title}
                      onChange={(e) => setNewTicketData(prev => ({...prev, title: e.target.value}))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="customer">{t('ops.tickets.customerLabel')}</Label>
                    <Input
                      id="customer"
                      placeholder={t('ops.tickets.customerPlaceholder')}
                      value={newTicketData.customer}
                      onChange={(e) => setNewTicketData(prev => ({...prev, customer: e.target.value}))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">{t('ops.tickets.category')}</Label>
                    <Select value={newTicketData.category} onValueChange={(value) => setNewTicketData(prev => ({...prev, category: value}))}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('ops.tickets.selectCategory')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="technical">{t('ops.tickets.catTechnical')}</SelectItem>
                        <SelectItem value="billing">{t('ops.tickets.catBilling')}</SelectItem>
                        <SelectItem value="general">{t('ops.tickets.catGeneral')}</SelectItem>
                        <SelectItem value="feature">{t('ops.tickets.catFeature')}</SelectItem>
                        <SelectItem value="bug">{t('ops.tickets.catBug')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="priority">{t('ops.common.priority')}</Label>
                    <Select value={newTicketData.priority} onValueChange={(value) => setNewTicketData(prev => ({...prev, priority: value}))}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('ops.tickets.selectPriority')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">{t('ops.tickets.prioLow')}</SelectItem>
                        <SelectItem value="medium">{t('ops.tickets.prioMedium')}</SelectItem>
                        <SelectItem value="high">{t('ops.tickets.prioHigh')}</SelectItem>
                        <SelectItem value="urgent">{t('ops.tickets.prioUrgent')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="description">{t('ops.tickets.descriptionLabel')}</Label>
                    <Textarea
                      id="description"
                      placeholder={t('ops.tickets.descriptionPlaceholder')}
                      value={newTicketData.description}
                      onChange={(e) => setNewTicketData(prev => ({...prev, description: e.target.value}))}
                    />
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={() => setNewTicketOpen(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button onClick={handleCreateTicket}>
                      {t('ops.tickets.createTicket')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Filter className="h-4 w-4 mr-2" />
                  {t('common.filters')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('ops.tickets.filterTickets')}</DialogTitle>
                  <DialogDescription>
                    {t('ops.tickets.filterTicketsDesc')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="status-filter">{t('ops.common.status')}</Label>
                    <Select value={selectedStatus} onValueChange={(value) => handleFiltersApply('status', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('ops.tickets.selectStatus')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('ops.tickets.allStatus')}</SelectItem>
                        <SelectItem value="open">{t('ops.tickets.statusOpen')}</SelectItem>
                        <SelectItem value="in_progress">{t('ops.tickets.statusInProgress')}</SelectItem>
                        <SelectItem value="resolved">{t('ops.tickets.statusResolved')}</SelectItem>
                        <SelectItem value="closed">{t('ops.tickets.statusClosed')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="priority-filter">{t('ops.common.priority')}</Label>
                    <Select value={selectedPriority} onValueChange={(value) => handleFiltersApply('priority', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('ops.tickets.selectPriority')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('ops.tickets.allPriorities')}</SelectItem>
                        <SelectItem value="critical">{t('ops.tickets.prioCritical')}</SelectItem>
                        <SelectItem value="high">{t('ops.tickets.prioHigh')}</SelectItem>
                        <SelectItem value="medium">{t('ops.tickets.prioMedium')}</SelectItem>
                        <SelectItem value="low">{t('ops.tickets.prioLow')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={() => setFiltersOpen(false)}>
                      {t('common.close')}
                    </Button>
                    <Button onClick={() => setFiltersOpen(false)}>
                      {t('ops.tickets.applyFilters')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Response Dialog */}
        <Dialog open={respondOpen} onOpenChange={setRespondOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('ops.tickets.respondToTicket')}</DialogTitle>
              <DialogDescription>
                {t('ops.tickets.respondDesc', { customer: selectedTicket?.customer || t('ops.tickets.theCustomer') })}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="response">{t('ops.tickets.responseMessage')}</Label>
                <Textarea
                  id="response"
                  placeholder={t('ops.tickets.responsePlaceholder')}
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  rows={4}
                />
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setRespondOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleSendResponse}>
                  {t('ops.tickets.sendResponse')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Escalation Dialog */}
        <Dialog open={escalateOpen} onOpenChange={setEscalateOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('ops.tickets.escalateTicket')}</DialogTitle>
              <DialogDescription>
                {t('ops.tickets.escalateDesc', { id: selectedTicket?.id })}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="escalate-reason">{t('ops.tickets.escalationReason')}</Label>
                <Textarea
                  id="escalate-reason"
                  placeholder={t('ops.tickets.escalationReasonPlaceholder')}
                  value={escalateReason}
                  onChange={(e) => setEscalateReason(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setEscalateOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleSendEscalation}>
                  {t('ops.tickets.escalateTicket')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder={t('ops.tickets.searchTickets')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Tickets List */}
        <div className="w-1/2 border-r border-gray-200 bg-white/70 backdrop-blur-sm overflow-hidden flex flex-col">
          <Tabs value={selectedStatus} onValueChange={setSelectedStatus} className="h-full flex flex-col">
            <TabsList className="grid w-full grid-cols-4 m-4">
              <TabsTrigger value="all" className="text-xs">
                {t('ops.tickets.tabAll')} ({statusCounts.all})
              </TabsTrigger>
              <TabsTrigger value="active" className="text-xs">
                {t('ops.tickets.tabActive')} ({statusCounts.active})
              </TabsTrigger>
              <TabsTrigger value="pending" className="text-xs">
                {t('ops.tickets.tabPending')} ({statusCounts.pending})
              </TabsTrigger>
              <TabsTrigger value="resolved" className="text-xs">
                {t('ops.tickets.tabResolved')} ({statusCounts.resolved})
              </TabsTrigger>
            </TabsList>
            {myStaffId && (
              <div className="px-4 pb-2">
                <Button
                  size="sm"
                  variant={myQueueOnly ? "default" : "outline"}
                  className="w-full text-xs"
                  onClick={() => setMyQueueOnly(v => !v)}
                >
                  <UserCheck className="h-3 w-3 mr-1" />
                  {myQueueOnly ? t('ops.tickets.showingMyQueue') : t('ops.tickets.myQueue')}
                </Button>
              </div>
            )}

            <TabsContent value={selectedStatus} className="flex-1 overflow-hidden">
              <div className="h-full overflow-y-auto px-4 pb-4">
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                      <Loader2 className="h-8 w-8 animate-spin text-purple-600 mx-auto mb-4" />
                      <p className="text-gray-600">{t('ops.tickets.loadingTickets')}</p>
                    </div>
                  </div>
                ) : error ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                      <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
                      <p className="text-red-600 mb-4">{error}</p>
                      <Button onClick={loadConversations} variant="outline">
                        {t('ops.common.tryAgain')}
                      </Button>
                    </div>
                  </div>
                ) : filteredTickets.length === 0 ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                      <MessageSquare className="h-8 w-8 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">{t('ops.tickets.noTicketsFound')}</p>
                      <p className="text-sm text-gray-500">
                        {searchTerm || selectedStatus !== 'all'
                          ? t('ops.tickets.adjustSearch')
                          : t('ops.tickets.newTicketsAppear')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredTickets.map((ticket) => (
                    <Card
                      key={ticket.id}
                      className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                        selectedTicket?.id === ticket.id
                          ? 'ring-2 ring-purple-500 bg-purple-50'
                          : 'bg-white hover:bg-gray-50'
                      }`}
                      onClick={() => { setSelectedTicket(ticket); loadTicketMessages(ticket); }}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-gradient-to-r from-green-400 to-purple-500 text-white font-semibold text-sm">
                                {getDisplayName(ticket).charAt(0).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <h4 className="font-medium text-gray-900 truncate">
                                {getDisplayName(ticket)}
                              </h4>
                              <p className="text-sm text-gray-600">{ticket.category || ticket.source || '—'}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(ticket.status)}
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {(ticket.created_at || ticket.timestamp) ? formatDate(ticket.created_at || ticket.timestamp) : '—'}
                            </span>
                          </div>
                        </div>

                        <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                          {ticket.lastMessage}
                        </p>

                        <div className="flex items-center justify-between">
                          <div className="flex space-x-2">
                            <Badge className={getStatusColor(ticket.status)} variant="secondary">
                              {ticket.status.replace('_', ' ')}
                            </Badge>
                            <Badge className={getPriorityColor(ticket.priority)} variant="secondary">
                              {ticket.priority}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            {ticket.unreadCount > 0 && (
                              <Badge className="bg-red-500 text-white">
                                {ticket.unreadCount}
                              </Badge>
                            )}
                            <ArrowRight className="h-4 w-4 text-gray-400" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Ticket Details */}
        <div className="flex-1 bg-white min-h-0 overflow-hidden">
          {selectedTicket ? (
            <div className="h-full flex flex-col min-h-0">
              {/* Ticket Header */}
              <Card className="rounded-none border-x-0 border-t-0">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-gradient-to-r from-green-400 to-purple-500 text-white font-semibold">
                          {getDisplayName(selectedTicket).charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <CardTitle className="text-xl">
                          {getDisplayName(selectedTicket)}
                        </CardTitle>
                        <p className="text-gray-600">{selectedTicket.category || selectedTicket.source || '—'}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={handleCallCustomer}>
                        <Phone className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleMessageCustomer}
                              title={t('ops.tickets.message')} aria-label={t('ops.tickets.message')}>
                        <MessageSquare className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  {/* Row 1: Status, Priority, Phone */}
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500 text-xs">{t('ops.common.status')}</span>
                      <div className="mt-1">
                        <Badge className={getStatusColor(selectedTicket.ticket_status || selectedTicket.status)}>
                          {(selectedTicket.ticket_status || selectedTicket.status || '').replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500 text-xs">{t('ops.common.priority')}</span>
                      <div className="mt-1">
                        <Badge className={getPriorityColor(selectedTicket.priority)}>
                          {selectedTicket.priority || '—'}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500 text-xs flex items-center gap-1"><Phone className="h-3 w-3" />{t('ops.tickets.phone')}</span>
                      <div className="mt-1 font-medium text-xs truncate">
                        {_isDisplayablePhone(selectedTicket.customer_phone) ? selectedTicket.customer_phone : '—'}
                      </div>
                    </div>
                  </div>
                  {/* Row 2: Assigned To, Assigned By */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500 text-xs flex items-center gap-1"><UserCheck className="h-3 w-3" />{t('ops.common.assignedTo')}</span>
                      <div className="mt-1 font-medium text-xs">
                        {displayStaffName(selectedTicket.assigned_to_name, null) || (selectedTicket.assigned_to ? STAFF_FALLBACK : <span className="text-gray-400">{t('ops.common.unassigned')}</span>)}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500 text-xs">{t('ops.tickets.assignedBy')}</span>
                      <div className="mt-1 font-medium text-xs text-gray-600">
                        {displayStaffName(selectedTicket.assigned_by_name, null) || (selectedTicket.assigned_by ? STAFF_FALLBACK : '—')}
                      </div>
                    </div>
                  </div>
                  {/* Row 3: Created, Updated, Accepted, Resolved */}
                  <div className="grid grid-cols-2 gap-3 text-xs text-gray-500">
                    <div>{t('ops.tickets.created')} <span className="text-gray-700 font-medium">{selectedTicket.created_at ? formatDate(selectedTicket.created_at) : '—'}</span></div>
                    <div>{t('ops.tickets.updated')} <span className="text-gray-700 font-medium">{selectedTicket.updated_at ? formatDate(selectedTicket.updated_at) : '—'}</span></div>
                    {selectedTicket.accepted_at && (
                      <div>{t('ops.tickets.acceptedBy')} <span className="text-blue-700 font-medium">{displayStaffName(selectedTicket.accepted_by_name)}</span> {formatDate(selectedTicket.accepted_at)}</div>
                    )}
                    {selectedTicket.resolved_at && (
                      <div>{t('ops.tickets.resolvedBy')} <span className="text-green-700 font-medium">{displayStaffName(selectedTicket.resolved_by_name)}</span> {formatDate(selectedTicket.resolved_at)}</div>
                    )}
                    {selectedTicket.reassigned_at && (
                      <div>{t('ops.tickets.reassignedBy')} <span className="text-purple-700 font-medium">{displayStaffName(selectedTicket.reassigned_by_name)}</span> {formatDate(selectedTicket.reassigned_at)}</div>
                    )}
                  </div>
                  {selectedTicket.assigned_at && (
                    <div className="text-xs text-gray-500">{t('ops.tickets.assignedAt')} <span className="text-gray-700 font-medium">{formatDate(selectedTicket.assigned_at)}</span></div>
                  )}
                </CardContent>
              </Card>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-4">
                  {messagesLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-purple-600 mr-2" />
                      <span className="text-gray-500">{t('ops.tickets.loadingConversation')}</span>
                    </div>
                  ) : selectedTicketMessages !== null ? (
                    selectedTicketMessages.length > 0 ? (
                      selectedTicketMessages.map((message, idx) => {
                        const isCustomer = message.sender_type === 'customer';
                        const isAI = message.sender_type === 'ai';
                        const isSystem = message.sender_type === 'system';
                        const text = message.message || message.content || '';
                        const ts = message.timestamp;
                        // System events (assignment, call, resolution, etc.) render as
                        // a centered timeline marker — not a chat bubble.
                        if (isSystem) {
                          return (
                            <div key={message.id || message.message_id || idx} className="flex items-center justify-center py-1">
                              <span className="text-xs text-gray-400 italic bg-gray-100 rounded-full px-3 py-1">
                                ⚙ {text}
                              </span>
                            </div>
                          );
                        }
                        return (
                          <div key={message.id || message.message_id || idx} className="flex items-start space-x-3">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className={
                                isCustomer ? "bg-blue-100 text-blue-600" :
                                isAI ? "bg-purple-100 text-purple-600" :
                                "bg-green-100 text-green-600"
                              }>
                                {isCustomer ? <User className="h-4 w-4" /> : isAI ? 'AI' : 'OP'}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="font-medium text-gray-900">
                                  {isCustomer ? getDisplayName(selectedTicket) :
                                   isAI ? t('ops.common.aiAgent') : t('role.operator')}
                                </span>
                                <span className="text-sm text-gray-500">
                                  {ts ? formatDate(ts) : '—'}
                                </span>
                              </div>
                              <div className={`rounded-lg p-3 ${isCustomer ? 'bg-blue-50' : isAI ? 'bg-purple-50' : 'bg-gray-50'}`}>
                                <p className="text-gray-700">{text}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-12">
                        <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('ops.tickets.noMessages')}</h3>
                        <p className="text-gray-500">{t('ops.tickets.noConvHistory')}</p>
                      </div>
                    )
                  ) : (
                    <div className="text-center py-12">
                      <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">{t('ops.tickets.selectTicket')}</h3>
                      <p className="text-gray-500">{t('ops.tickets.clickTicketLoad')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-2">
                {/* Primary actions */}
                <div className="flex space-x-2">
                  <Button
                    className="flex-1 bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
                    onClick={handleRespond}
                    size="sm"
                  >
                    {t('ops.tickets.respond')}
                  </Button>
                  {/* GOV-7 — Escalate + Mark Resolved are HIDDEN on terminal tickets (already
                      resolved/closed). Respond stays available. Lifecycle buttons below are unchanged. */}
                  {!['resolved', 'closed'].includes(selectedTicket.ticket_status || selectedTicket.status) && (
                    <>
                      <Button variant="outline" size="sm" onClick={handleEscalate}>
                        {t('ops.tickets.escalate')}
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleMarkResolved}>
                        {t('ops.tickets.markResolved')}
                      </Button>
                    </>
                  )}
                </div>
                {/* Lifecycle actions */}
                <div className="flex flex-wrap gap-2">
                  {/* Accept — assigned operator or admin/manager */}
                  {selectedTicket.ticket_id?.startsWith('TKT-') &&
                   ['assigned', 'open'].includes(selectedTicket.ticket_status) &&
                   (myStaffId === selectedTicket.assigned_to || _ASSIGN_ROLES.has(myRole)) && (
                    <Button
                      variant="outline" size="sm"
                      className="border-blue-200 text-blue-700 hover:bg-blue-50"
                      onClick={() => handleAcceptTicket(selectedTicket)}
                    >
                      <CheckCheck className="h-3 w-3 mr-1" />{t('ops.tickets.accept')}
                    </Button>
                  )}
                  {/* Reassign — operator can reassign their own; admin/manager can reassign any */}
                  {selectedTicket.ticket_id?.startsWith('TKT-') &&
                   !['resolved', 'closed', 'deleted'].includes(selectedTicket.ticket_status) &&
                   (myStaffId === selectedTicket.assigned_to || _ASSIGN_ROLES.has(myRole)) && (
                    <Button
                      variant="outline" size="sm"
                      className="border-amber-200 text-amber-700 hover:bg-amber-50"
                      onClick={() => openReassignDialog(selectedTicket)}
                    >
                      <UserPlus className="h-3 w-3 mr-1" />{t('ops.tickets.reassign')}
                    </Button>
                  )}
                  {/* Assign to new operator — admin/manager/administrator only */}
                  {_ASSIGN_ROLES.has(myRole) && (
                    <Button
                      variant="outline" size="sm"
                      className="border-purple-200 text-purple-700 hover:bg-purple-50"
                      onClick={() => openAssignDialog(selectedTicket)}
                    >
                      <UserPlus className="h-3 w-3 mr-1" />{t('ops.common.assign')}
                    </Button>
                  )}
                  {/* Delete Invalid Guest — only for tickets with no valid customer identity */}
                  {_DELETE_ROLES.has(myRole) &&
                   selectedTicket.ticket_id?.startsWith('TKT-') &&
                   (() => {
                     const name = (selectedTicket.customer_name || '').trim().toLowerCase();
                     const phone = (selectedTicket.customer_phone || '').trim();
                     const _GUEST_NAMES = new Set(['guest', 'unknown', 'anonymous', 'customer', 'undefined', 'null', '', 'none']);
                     const nameIsInvalid = _GUEST_NAMES.has(name) || /^[a-z0-9]{20,36}$/.test(name) || name.startsWith('cust_');
                     return nameIsInvalid && !phone;
                   })() && (
                    <Button
                      variant="outline" size="sm"
                      className="border-red-200 text-red-700 hover:bg-red-50 ml-auto"
                      onClick={() => handleDeleteInvalidGuest(selectedTicket)}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />{t('ops.tickets.deleteGuest')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <ArrowRight className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">{t('ops.tickets.selectTicket')}</h3>
                <p className="text-sm">{t('ops.tickets.chooseTicketRespond')}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* GOV-7 Governed Resolve Dialog — MANDATORY meaningful Resolution Steps (solution_resolution
          for Solution Bank review). The backend creates the Solution candidate before finalizing;
          blank/placeholder steps are blocked client-side and the ticket stays unresolved. */}
      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Resolve Ticket</DialogTitle>
            <DialogDescription>
              {t('ops.tickets.respondDesc', { customer: getDisplayName(selectedTicket || {}) })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 p-3 space-y-3">
              <div>
                <Label htmlFor="resolve-steps">Resolution Steps (required)</Label>
                <Textarea
                  id="resolve-steps"
                  placeholder="Describe the exact, reusable steps that resolved the issue (one per line). Not a status like 'done'."
                  value={resolveData.resolutionSteps}
                  onChange={(e) => setResolveData(prev => ({ ...prev, resolutionSteps: e.target.value }))}
                  rows={4}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="resolve-title">Solution Title (optional)</Label>
                <Input
                  id="resolve-title"
                  placeholder="Short problem title"
                  value={resolveData.solutionTitle}
                  onChange={(e) => setResolveData(prev => ({ ...prev, solutionTitle: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <p className="text-xs text-gray-500">
                {t('ops.esc.approveBeforeAi')}
              </p>
            </div>
            <div>
              <Label htmlFor="resolve-note">Resolution Note (optional)</Label>
              <Textarea
                id="resolve-note"
                placeholder="Internal note about how this ticket was resolved."
                value={resolveData.resolution}
                onChange={(e) => setResolveData(prev => ({ ...prev, resolution: e.target.value }))}
                rows={2}
                className="mt-1"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <Button variant="outline" onClick={() => setResolveDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={submitGovernedResolve}>
                {t('ops.tickets.markResolved')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reassign Ticket Dialog — operator reassigns own ticket; management reassigns any */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('ops.tickets.reassignTicket')}</DialogTitle>
            <DialogDescription>
              {t('ops.tickets.reassignDesc', { id: reassignTicketId })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('ops.tickets.reassignTo')}</Label>
              <Select value={reassignTarget} onValueChange={setReassignTarget}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('ops.common.selectOperator')} />
                </SelectTrigger>
                <SelectContent>
                  {staffList
                    .filter(s => s.id !== myStaffId && s.role && ['operator', 'analyst', 'it', 'integration'].includes(s.role.toLowerCase()))
                    .map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name || s.id} ({s.role})
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('ops.tickets.reasonOptional')}</Label>
              <Textarea
                placeholder={t('ops.tickets.reassignReasonPlaceholder')}
                value={reassignReason}
                onChange={(e) => setReassignReason(e.target.value)}
                rows={2}
                className="mt-1"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <Button variant="outline" onClick={() => setReassignDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleReassignTicket} className="bg-amber-600 hover:bg-amber-700">
                <UserPlus className="h-4 w-4 mr-2" />{t('ops.tickets.reassign')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Ticket Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('ops.tickets.assignTicket')}</DialogTitle>
            <DialogDescription>
              {t('ops.tickets.assignDesc', { id: assignTicketId })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('ops.tickets.assignToLabel')}</Label>
              <Select value={assignTarget} onValueChange={setAssignTarget}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('ops.common.selectOperator')} />
                </SelectTrigger>
                <SelectContent>
                  {staffList
                    .filter(s => s.role && ['operator', 'analyst', 'it', 'integration'].includes(s.role.toLowerCase()))
                    .map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name || s.id} ({s.role})
                      </SelectItem>
                    ))
                  }
                  {staffList.filter(s => s.role && ['operator', 'analyst', 'it', 'integration'].includes(s.role.toLowerCase())).length === 0 && (
                    <SelectItem value="__none__" disabled>{t('ops.tickets.noOperatorsTenant')}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleAssignTicket}>
                <UserPlus className="h-4 w-4 mr-2" />
                {t('ops.tickets.assignTicket')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketsManager;